import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui/button/index.js";
import { useReducedMotion } from "../../../../lib/motion.js";
import type { TemplateButtonFormState } from "../template-management-form.js";

const BUTTON_TYPES = ["QUICK_REPLY", "URL", "PHONE_NUMBER", "VOICE_CALL"] as const;

export interface TemplateButtonEditorProps {
  buttons: TemplateButtonFormState[];
  onChange: (buttons: TemplateButtonFormState[]) => void;
  disabled?: boolean;
}

const labelClass = "grid gap-1 text-[0.85rem] text-muted";
const inputClass = "w-full min-w-0 rounded-sm border border-border bg-surface px-3 py-2 text-foreground";

export function TemplateButtonEditor({
  buttons,
  onChange,
  disabled = false,
}: TemplateButtonEditorProps): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const pendingFocusIndexRef = useRef<number | null>(null);
  const blockRefs = useRef<Array<HTMLDivElement | null>>([]);

  function updateButton(index: number, next: TemplateButtonFormState): void {
    const copy = [...buttons];
    copy[index] = next;
    onChange(copy);
  }

  function removeButton(index: number): void {
    onChange(buttons.filter((_, i) => i !== index));
  }

  function addButton(type: TemplateButtonFormState["type"]): void {
    pendingFocusIndexRef.current = buttons.length;
    switch (type) {
      case "QUICK_REPLY":
        onChange([...buttons, { type, text: "" }]);
        break;
      case "URL":
        onChange([...buttons, { type, text: "", url: "" }]);
        break;
      case "PHONE_NUMBER":
        onChange([...buttons, { type, text: "", phoneNumber: "" }]);
        break;
      case "VOICE_CALL":
        onChange([...buttons, { type, text: "" }]);
        break;
      default: {
        pendingFocusIndexRef.current = null;
        const _exhaustive: never = type;
        return _exhaustive;
      }
    }
  }

  useLayoutEffect(() => {
    const index = pendingFocusIndexRef.current;
    if (index == null) return;
    pendingFocusIndexRef.current = null;
    const block = blockRefs.current[index];
    if (!block) return;

    block.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    });
    const input = block.querySelector<HTMLInputElement>("input:not([disabled])");
    input?.focus({ preventScroll: true });
  }, [buttons, reduceMotion]);

  return (
    <div className="grid gap-3">
      {buttons.length === 0 ? (
        <p className="m-0 text-[0.85rem] text-muted">{t("whatsapp.templates.manage.buttons.empty")}</p>
      ) : null}

      {buttons.map((button, index) => (
        <div
          key={`button-${index}`}
          ref={(node) => {
            blockRefs.current[index] = node;
          }}
          className="grid scroll-mt-3 gap-2 rounded-md border border-border bg-surface-muted p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.85rem] font-semibold">
              {t(`whatsapp.templates.manage.buttons.types.${button.type}`)}
            </span>
            <Button
              type="button"
              variant="link"
              linkDestructive
              onClick={() => removeButton(index)}
              disabled={disabled}
            >
              {t("whatsapp.templates.manage.buttons.remove")}
            </Button>
          </div>

          <label className={labelClass}>
            {t("whatsapp.templates.manage.buttons.label")}
            <input
              className={inputClass}
              value={button.text}
              maxLength={25}
              disabled={disabled}
              onChange={(event) => updateButton(index, { ...button, text: event.target.value })}
            />
          </label>

          {button.type === "URL" ? (
            <label className={labelClass}>
              {t("whatsapp.templates.manage.buttons.url")}
              <input
                className={inputClass}
                value={button.url}
                maxLength={2000}
                disabled={disabled}
                onChange={(event) =>
                  updateButton(index, { ...button, url: event.target.value })
                }
              />
            </label>
          ) : null}

          {button.type === "PHONE_NUMBER" ? (
            <label className={labelClass}>
              {t("whatsapp.templates.manage.buttons.phoneNumber")}
              <input
                className={inputClass}
                value={button.phoneNumber}
                maxLength={32}
                inputMode="tel"
                autoComplete="tel"
                placeholder={t("whatsapp.templates.manage.buttons.phonePlaceholder")}
                disabled={disabled}
                onChange={(event) =>
                  updateButton(index, { ...button, phoneNumber: event.target.value })
                }
              />
              <span className="text-[0.8rem] leading-[1.35] text-muted">
                {t("whatsapp.templates.manage.buttons.phoneHelp")}
              </span>
            </label>
          ) : null}
        </div>
      ))}

      <div className="max-w-64">
        <label className={labelClass}>
          {t("whatsapp.templates.manage.buttons.add")}
          <select
            className="w-full rounded-sm border border-border bg-surface px-3 py-2 text-foreground"
            value=""
            disabled={disabled || buttons.length >= 10}
            onChange={(event) => {
              const value = event.target.value as TemplateButtonFormState["type"] | "";
              if (!value) return;
              addButton(value);
            }}
          >
            <option value="">{t("whatsapp.templates.manage.buttons.addPlaceholder")}</option>
            {BUTTON_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`whatsapp.templates.manage.buttons.types.${type}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
